<template>
  <div class="wrap">

    <!-- Header -->
    <b-container fluid>
      <b-row v-show="!loading">
        <b-col sm="8"
               md="6"
               lg="4">
          <esg-single-score-card :title="`당해 연도 배출량 (${currentYear}년)`"
                                 :score="currentYearActual"
                                 score-unit="tCO2"
                                 :diff="Math.round(currentYearReductionRate * 10) / 10"
                                 :diff-unit="`% ${currentYearReductionRate >= 0 ? '초과' : '감축'}`"
                                 :color="`${currentYearReductionRate >= 0 ? 'red' : 'green'}`"
                                 :arrow="false">
          </esg-single-score-card>
        </b-col>
        <b-col sm="8"
               md="6"
               lg="4">
          <esg-single-score-card title="단기 목표 배출량 (SBTI 기준 - 2030년)"
                                 :score="formatNumber(shortTermTarget, 0)"
                                 score-unit="tCO2e"
                                 :color=colors[2]>
            <template #annotation>
              <b class="mx-2 annotation">* BAU 대비 {{ formatNumber(shortTermReductionRate, 0) }}% 감축</b>
            </template>
          </esg-single-score-card>
        </b-col>
        <b-col sm="8"
               md="6"
               lg="4">
          <esg-single-score-card title="장기 목표 배출량 (SBTI 기준 - 2050년)"
                                 :score="formatNumber(longTermTarget, 0)"
                                 score-unit="tCO2e"
                                 :color=colors[2]>
            <template #annotation>
              <b class="mx-2 annotation">* BAU 대비 {{ formatNumber(longTermReductionRate, 0) }}% 감축</b>
            </template>
          </esg-single-score-card>
        </b-col>
      </b-row>
    </b-container>
    <!-- /Header -->

    <!-- Chart -->
    <esg-base-chart max-width="1000px"
                    height="330px">
      <!-- Spinner -->
      <loading :active="loading"
               :z-index="1000"
               :is-full-page="false"/>
      <!-- Spinner -->

      <apex-chart ref="chart"
                  v-show="!loading"
                  type="line"
                  height="300"
                  :options="chartOptions"
                  :series="series"/>
    </esg-base-chart>
    <!-- /Chart -->

    <!-- Table -->
    <esg-table-without-meta size="xs"
                            v-show="!loading"
                            :items="tableItems"
                            :fields="tableFields"
                            max-height="250px">
      <template #cell(scope)="{ value, item, row }">
        <!-- Merge cells every two rows -->
        <td v-if="row % 2 === 0"
            class="text-center"
            rowspan="2">{{ value }}
        </td>
        <td v-else
            class="d-none"></td>
      </template>
    </esg-table-without-meta>
    <!-- /Table -->
  </div>
</template>

<script>
import EsgBaseChart from "@/components/esg/base/EsgBaseChart.vue";
import EsgTableWithoutMeta from "@/components/esg/base/EsgTableWithoutMeta.vue";
import EsgSingleScoreCard from "@/components/esg/base/EsgSingleScoreCard.vue";

export default {
  components: { EsgSingleScoreCard, EsgTableWithoutMeta, EsgBaseChart },
  props: {
    colors: {
      type: Array,
      default() {
        return ["#FBD308", "#2d9d6b", "#3F84E5", "#FF4560", "#775DD0", "#FEB019", "#00E396", "#008FFB"]
      }
    }
  },
  data() {
    return {
      loading: false,
      firstYear: 2020,
      lastYear: 2050,
      series: [],
      shortTermTarget: 0,
      longTermTarget: 0,
      currentYearActual: 0,
      currentYearTarget: 0,
      shortTermReductionRate: 0,
      longTermReductionRate: 0,
      currentYearReductionRate: 0,
      tableItems: [],
      tableFields: [],
      chartOptions: {
        chart: {
          type: 'line',
          stacked: true,
          fontFamily: 'NanumGothic, serif',
          toolbar: { show: false },
          zoom: { enabled: false }
        },
        plotOptions: {
          bar: {
            borderRadius: 0,
            horizontal: false,
            columnWidth: '100%',
            dataLabels: {
              total: {
                enabled: false,
                formatter: (val) => this.compactFormatNumber(val),
                style: {
                  color: 'black',
                  fontSize: '.5rem',
                  fontWeight: 400,
                }
              }
            }
          },
        },
        yaxis: {
          show: true,
          tickAmount: 6,
          forceNiceScale: false,
          crosshairs: { show: false },
          title: {
            text: '탄소 배출량 (tCO2)',
            style: {
              fontSize: 12,
              fontWeight: 'normal'
            }
          },
          labels: {
            formatter: (val) => {
              const formatter = Intl.NumberFormat('en', { notation: 'compact' })
              return formatter.format(val);
            },
          }
        },
        labels: [],
        xaxis: {
          type: 'datetime',
          crosshairs: { show: false },
          tooltip: { enabled: false }
        },
        grid: {
          padding: {
            right: 30,
          }
        },
        dataLabels: {
          enabled: false,
        },
        tooltip: {
          enabled: true,
          shared: true,
          intersect: false,
          x: {
            formatter: (val) => new Date(val).getFullYear()
          },
          y: {
            formatter: val => val ? this.formatNumber(val, 2) + " tCO2" : null
          }
        },
        legend: {
          position: 'bottom',
          offsetY: 5
        },
        stroke: {
          colors: ['transparent', 'transparent', this.colors[2]],
          show: true,
          width: [1, 1, 3]
        },
        markers: {
          size: 3,
          colors: ['#FFF'],
          strokeColors: this.colors[2],
          strokeWidth: 1,
          shape: "circle",
          hover: {
            sizeOffset: 0,
          }
        },
        annotations: {
          points: []
        }
      },
    }
  },
  created() {
    this.fetchData();
  },
  methods: {
    async fetchData() {
      try {
        this.loading = true;

        const years = Array.from({ length: this.lastYear - this.firstYear + 1 }, (_, index) => this.firstYear + index);

        // Construct arguments used to fetch SBTi data
        let args = [{ years }];
        let data = new FormData();
        data.append('path', 'esg/target/sbti/main');
        data.append('json', JSON.stringify(args));

        // Fetch SBTi data
        this.abortController = new AbortController();
        let response = await axios.post('esg/lca/run/python/chart-data', data, {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal
        });
        if (!response.data.data) {
          this.series = [];
          return;
        }
        let sbti = JSON.parse(response.data.data)[0].data;

        // Construct arguments to fetch actual GHG emission data
        args = [{ years, scopes: [1, 2], groupKeys: ['year', 'scope'] }];
        data = new FormData();
        data.append('path', 'esg/ghg_emission/main');
        data.append('json', JSON.stringify(args));
        response = await axios.post('esg/lca/run/python/chart-data', data, {
          headers: { Authorization: accessToken() },
          signal: this.abortController.signal
        });
        if (!response.data.data) {
          this.series = [];
          return;
        }
        let actual = JSON.parse(response.data.data)[0].data;

        this.setSeries(sbti, actual, years);
        this.setLabels(years);
        this.setAnnotations();
      } catch (error) {
        this.loading = false;
        await this.showError(error);
      } finally {
        this.loading = false;
        this.refreshChart();
      }
    },
    setSeries(sbti, actual, years) {
      let scope1Actuals = [];
      let scope2Actuals = [];
      let scope1Targets = [];
      let scope2Targets = [];
      let actuals = [];
      let targets = [];
      let sbtiTargets = [];

      years.forEach((year) => {
        let scope1ActualData = actual.find(row => row.year === year && row.scope === 1);
        let scope2ActualData = actual.find(row => row.year === year && row.scope === 2);
        let sbtiData = sbti.find(row => row.year === year);

        let scope1Actual = scope1ActualData ? scope1ActualData.values : 0;
        let scope2Actual = scope2ActualData ? scope2ActualData.values : 0;
        let scope1Target = sbtiData ? sbtiData.scope1_target : 0;
        let scope2Target = sbtiData ? sbtiData.scope2_target : 0;
        let sbtiTarget = sbtiData ? sbtiData.sbti_target : 0;

        scope1Actuals.push(scope1Actual);
        scope2Actuals.push(scope2Actual);
        scope1Targets.push(scope1Target);
        scope2Targets.push(scope2Target);
        targets.push(scope1Target + scope2Target);
        actuals.push(scope1Actual + scope2Actual);
        sbtiTargets.push(sbtiTarget);
      });

      this.series = [
        {
          name: '목표 배출량',
          type: 'column',
          group: 'target',
          data: targets
        },
        {
          name: '실제 배출량',
          type: 'column',
          group: 'actual',
          data: actuals
        },
        {
          name: 'SBTi 기준 목표 배출량',
          type: 'line',
          group: 'sbti',
          data: sbtiTargets
        },
      ];

      // Set the maximum of the y-axis scale
      this.chartOptions.yaxis.max = Math.max(
          ...sbtiTargets,
          ...targets,
          ...actuals
      ) + .5e6;

      // Short and long-term targets
      let currentYearIndex = this.currentYear - this.firstYear;
      this.currentYearTarget = sbtiTargets[currentYearIndex];
      this.shortTermTarget = sbtiTargets[10];
      this.longTermTarget = sbtiTargets[30];

      // Calculate reduction rate
      let bau = targets[0];
      let currentYearTarget = targets[currentYearIndex];
      this.currentYearActual = actuals[currentYearIndex];
      this.shortTermReductionRate = 100 * (bau - this.shortTermTarget) / bau;
      this.longTermReductionRate = 100 * (bau - this.longTermTarget) / bau;
      this.currentYearReductionRate = 100 * (this.currentYearActual / currentYearTarget - 1);

      // Set table items
      let items = [
        { scope: '합계', item: '목표' },
        { scope: '합계', item: '실제' },
        { scope: 'Scope 1', item: '목표' },
        { scope: 'Scope 1', item: '실제' },
        { scope: 'Scope 2', item: '목표' },
        { scope: 'Scope 2', item: '실제' },
      ];
      let fields = [
        { name: 'scope', label: 'Scope', list: true },
        { name: 'item', label: '항목', list: true }
      ];
      years.forEach((year, idx) => {
        items[0][year] = targets[idx];
        items[1][year] = actuals[idx];
        items[2][year] = scope1Targets[idx];
        items[3][year] = scope1Actuals[idx];
        items[4][year] = scope2Targets[idx];
        items[5][year] = scope2Actuals[idx];
        fields.push({
          name: `${year}`,
          label: `${year}`,
          formatter: val => this.formatNumber(val || '-', 0),
          list: true
        });
      });
      this.tableItems = items;
      this.tableFields = fields;
    },
    setLabels(years) {
      this.chartOptions.labels = years.map(year => `${year}-01-01`);
    },
    setAnnotations() {
      this.chartOptions.annotations.points = [{
        x: new Date('2030-01-01').getTime(),
        y: 0,
        marker: {
          size: 4,
          fillColor: "#fff",
          strokeColor: this.colors[2], // yellow
        },
        label: {
          borderColor: this.colors[2],
          style: {
            color: "#fff",
            background: this.colors[2]
          },
          text: "SBTi 단기목표"
        }
      },
        {
          x: new Date('2050-01-01').getTime(),
          y: 0,
          marker: {
            size: 4,
            fillColor: "#fff",
            strokeColor: this.colors[2],
          },
          label: {
            borderColor: this.colors[2],
            style: {
              color: "#fff",
              background: this.colors[2]
            },
            text: "SBTi 장기목표"
          }
        }];

      // Set short and long-term targets for annotations
      this.chartOptions.annotations.points[0].y = this.shortTermTarget;
      this.chartOptions.annotations.points[1].y = this.longTermTarget;
    },
    formatEmissionRelativeToTarget(emission) {
      return this.formatNumber(Math.abs(emission), 1) + (emission >= 0 ? '% 초과' : '% 감축')
    },
  }
}
</script>

<style scoped>
.wrap {
  width: 100%;
}

.target {
  font-size: 1rem;
}

.annotation {
  font-size: 0.875rem;
}

>>> span.badge.badge-primary {
  padding: .25rem .5rem !important;
  background-color: #5182DE;
  color: #FFF;
  font-weight: normal
}

.annotation {
  font-size: 10px;
  font-weight: 500;
  font-family: NanumGothic;
  color: #888;
}

[class^="col-"] {
  padding-left: 7px;
  padding-right: 7px;
}
</style>
